import React, { useState } from "react";

const blockOptions = {
    hero: [
        {
            id: "heroA",
            name: "Hero A – Left text, right form",
            description: "Conversion-focused hero with title, subtext and lead capture form.",
        },
        {
            id: "heroB",
            name: "Hero B – Centered with video",
            description: "Centered hero with big headline and video placeholder.",
        },
        {
            id: "heroC",
            name: "Hero C – Split gradient",
            description: "Gradient background with text on the left and feature bullets on the right.",
        },
    ],
    agents: [
        {
            id: "agentsA",
            name: "Agent cards – 3 in a row",
            description: "Profile cards with image, name, tags and CTA.",
        },
        {
            id: "agentsB",
            name: "Agent list – Compact",
            description: "Vertical list with status badge and contact actions.",
        },
    ],
    blog: [
        {
            id: "blogA",
            name: "Blog grid",
            description: "3-column grid of latest posts pulled from CMS/API.",
        },
        {
            id: "blogB",
            name: "Blog list",
            description: "Vertical list with category and read time.",
        },
    ],
    video: [
        {
            id: "videoA",
            name: "Livestream section",
            description: "Upcoming live events with Join buttons.",
        },
        {
            id: "videoB",
            name: "Video library",
            description: "On-demand video cards with filters.",
        },
    ],
    cta: [
        {
            id: "ctaA",
            name: "Simple CTA",
            description: "Single call-to-action with button and short text.",
        },
        {
            id: "ctaB",
            name: "Multi-column CTA",
            description: "3-column value props with CTA.",
        },
    ],
} as const;

const stepBadges = [
    { id: 1, label: "Build website" },
    { id: 2, label: "Connect channels" },
    { id: 3, label: "Go live" },
];

export default function CmsAndInnholdModule() {
    const [selectedBlocks, setSelectedBlocks] = useState<{ [key: string]: string | null }>({
        hero: "heroA",
        agents: "agentsA",
        blog: "blogA",
        video: null,
        cta: "ctaA",
    });

    const [mode, setMode] = useState<"blueprint" | "template">("blueprint");

    const handleSelect = (category: keyof typeof blockOptions, id: string) => {
        setSelectedBlocks((prev) => ({
            ...prev,
            [category]: prev[category] === id ? null : id,
        }));
    };

    const isSelected = (category: keyof typeof blockOptions, id: string) => {
        return selectedBlocks[category] === id;
    };

    // THEME STATE
    const [primaryColor, setPrimaryColor] = useState("#0C5FD1");
    const [secondaryColor, setSecondaryColor] = useState("#79AFFF");
    const [useGradient, setUseGradient] = useState(true);

    return (
        <div
            style={{
                "--primary-color": primaryColor,
                "--secondary-color": secondaryColor,
                "--gradient-bg": useGradient
                    ? `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
                    : primaryColor,
            } as React.CSSProperties}
        >
            <div className="min-h-screen bg-gradient-to-br from-[#B5D8FF] via-[#D2E8FF] to-[#F4F9FF] text-[#062150] flex flex-col relative">


                {/* Top bar */}
                <header className="border-b border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 backdrop-blur z-10">
                    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-xl bg-[var(--primary-color)]/10 border border-[var(--primary-color)]/50 flex items-center justify-center text-xs font-semibold text-[#062150]">
                                CC
                            </div>
                            <div>
                                <p className="text-sm font-semibold tracking-tight">Chamcore Site Builder</p>
                                <p className="text-xs text-slate-500">Microcomponents · APIs · Live preview</p>
                            </div>
                        </div>

                        {/* Theme Controls */}
                        <div className="top-3 justify-center flex items-center gap-4 bg-white/80 backdrop-blur px-3 py-2 rounded-xl border border-slate-300 z-20 shadow-sm">
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] text-slate-600">Primary</label>
                                <input
                                    type="color"
                                    value={primaryColor}
                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                    className="h-6 w-6 rounded cursor-pointer border border-slate-400"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] text-slate-600">Secondary</label>
                                <input
                                    type="color"
                                    value={secondaryColor}
                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                    className="h-6 w-6 rounded cursor-pointer border border-slate-400"
                                />
                            </div>
                            <label className="flex items-center gap-1 text-[11px] text-slate-600">
                                <input
                                    type="checkbox"
                                    checked={useGradient}
                                    onChange={(e) => setUseGradient(e.target.checked)}
                                />
                                Gradient
                            </label>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 rounded-full bg-[#E4F0FF] border border-slate-300 px-1 py-0.5 text-xs">
                                <button
                                    className={`px-2 py-1 rounded-full transition text-[11px] ${mode === "blueprint"
                                            ? "bg-[var(--primary-color)] text-white font-semibold"
                                            : "text-[#0C5FD1]"
                                        }`}
                                    onClick={() => setMode("blueprint")}
                                >
                                    Blueprint mode
                                </button>
                                <button
                                    className={`px-2 py-1 rounded-full transition text-[11px] ${mode === "template"
                                            ? "bg-[var(--primary-color)] text-white font-semibold"
                                            : "text-[#0C5FD1]"
                                        }`}
                                    onClick={() => setMode("template")}
                                >
                                    Template mode
                                </button>
                            </div>
                            <button className="text-xs px-3 py-1.5 rounded-full border border-slate-300 hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/70">
                                Preview mobile
                            </button>
                        </div>
                    </div>
                </header>

                {/* Stepper */}
                <div className="border-b border-slate-200 bg-[#E4F0FF]/60">
                    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs">
                            {stepBadges.map((step, index) => {
                                const active = index === 0;
                                const complete = index < 0;
                                return (
                                    <div key={step.id} className="flex items-center gap-2">
                                        <div
                                            className={`h-6 px-2 rounded-full border text-[11px] flex items-center gap-1 transition ${active
                                                    ? "border-[var(--primary-color)] bg-[var(--primary-color)]/10 text-[#0C5FD1]"
                                                    : "border-slate-300 text-[#0C5FD1] bg-white/60"
                                                }`}
                                        >
                                            <span
                                                className={`h-3 w-3 rounded-full border ${active
                                                        ? "border-[var(--primary-color)] bg-[var(--primary-color)]/70"
                                                        : complete
                                                            ? "border-emerald-400 bg-emerald-500/60"
                                                            : "border-slate-400 bg-[#79AFFF]"
                                                    }`}
                                            />
                                            <span className="uppercase tracking-wide">Step {step.id}</span>
                                            <span className="hidden sm:inline">· {step.label}</span>
                                        </div>
                                        {index < stepBadges.length - 1 && (
                                            <span className="text-[#0A4A99]">···</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-500">
                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                            Live microservice bindings ready
                        </div>
                    </div>
                </div>

                {/* Main layout */}
                <div className="flex-1 max-w-6xl mx-auto w-full px-4 py-4 flex flex-col lg:flex-row gap-4">
                    {/* Left: block selector */}
                    <aside className="w-full lg:w-72 shrink-0 h-[calc(100vh-160px)] sticky top-[160px] overflow-y-auto rounded-2xl border border-slate-200 bg-[#E4F0FF]/80 p-3 flex flex-col gap-3">
                        <div>
                            <p className="text-xs font-semibold text-[#0A4A99] mb-1">Microcomponent library</p>
                            <p className="text-[11px] text-slate-500 mb-2">
                                Tick the blocks you want. Chamcore wires the APIs, data and payments under the hood.
                            </p>
                        </div>

                        <div className="space-y-3 overflow-y-auto pr-1 max-h-[calc(100vh-210px)]">
                            {/* Hero options */}
                            <SectionHeader title="Hero" subtitle="First impression above the fold" />
                            <div className="space-y-1">
                                {blockOptions.hero.map((option) => (
                                    <BlockToggle
                                        key={option.id}
                                        category="hero"
                                        option={option}
                                        selected={isSelected("hero", option.id)}
                                        onToggle={handleSelect}
                                    />
                                ))}
                            </div>

                            {/* Agents */}
                            <SectionHeader title="Agents" subtitle="How your team shows up" />
                            <div className="space-y-1">
                                {blockOptions.agents.map((option) => (
                                    <BlockToggle
                                        key={option.id}
                                        category="agents"
                                        option={option}
                                        selected={isSelected("agents", option.id)}
                                        onToggle={handleSelect}
                                    />
                                ))}
                            </div>

                            {/* Blog */}
                            <SectionHeader title="Blog" subtitle="Content from your CMS" />
                            <div className="space-y-1">
                                {blockOptions.blog.map((option) => (
                                    <BlockToggle
                                        key={option.id}
                                        category="blog"
                                        option={option}
                                        selected={isSelected("blog", option.id)}
                                        onToggle={handleSelect}
                                    />
                                ))}
                            </div>

                            {/* Video */}
                            <SectionHeader title="Video & streaming" subtitle="Chamcore video + IVR" />
                            <div className="space-y-1">
                                {blockOptions.video.map((option) => (
                                    <BlockToggle
                                        key={option.id}
                                        category="video"
                                        option={option}
                                        selected={isSelected("video", option.id)}
                                        onToggle={handleSelect}
                                    />
                                ))}
                            </div>

                            {/* CTA */}
                            <SectionHeader title="Call-to-action" subtitle="Last push before footer" />
                            <div className="space-y-1 mb-2">
                                {blockOptions.cta.map((option) => (
                                    <BlockToggle
                                        key={option.id}
                                        category="cta"
                                        option={option}
                                        selected={isSelected("cta", option.id)}
                                        onToggle={handleSelect}
                                    />
                                ))}
                            </div>
                        </div>
                    </aside>

                    {/* Right: live preview */}
                    <main className="flex-1 rounded-2xl border border-slate-200 bg-white/80 overflow-hidden flex flex-col shadow-[0_18px_40px_rgba(15,23,42,0.12)]">
                        <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between text-[11px] text-slate-500 bg-[#F4F8FF]">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-300 bg-white">
                                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                    Live preview
                                </span>
                                <span>Client: demo.chamcore.app</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button className="px-2 py-0.5 rounded-full border border-slate-300 hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                                    Desktop
                                </button>
                                <button className="px-2 py-0.5 rounded-full border border-slate-300 hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                                    Tablet
                                </button>
                                <button className="px-2 py-0.5 rounded-full border border-slate-300 hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                                    Mobile
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 bg-[var(--gradient-bg)]/10 overflow-y-auto">
                            <div className="w-full max-w-4xl mx-auto p-4 space-y-6">
                                {/* HERO */}
                                {selectedBlocks.hero === "heroA" && <HeroA />}
                                {selectedBlocks.hero === "heroB" && <HeroB />}
                                {selectedBlocks.hero === "heroC" && <HeroC />}

                                {/* AGENTS */}
                                {selectedBlocks.agents === "agentsA" && <AgentsGrid />}
                                {selectedBlocks.agents === "agentsB" && <AgentsList />}

                                {/* BLOG */}
                                {selectedBlocks.blog === "blogA" && <BlogGrid />}
                                {selectedBlocks.blog === "blogB" && <BlogList />}

                                {/* VIDEO */}
                                {selectedBlocks.video === "videoA" && <VideoLivestream />}
                                {selectedBlocks.video === "videoB" && <VideoLibrary />}

                                {/* CTA */}
                                {selectedBlocks.cta === "ctaA" && <CtaSimple />}
                                {selectedBlocks.cta === "ctaB" && <CtaMulti />}

                                {/* Next step card */}
                                <div className="mt-6 rounded-2xl border border-slate-200 bg-[#E4F0FF] px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm font-semibold text-[#083570]">Next: connect Chamcore services</p>
                                        <p className="text-xs text-slate-500 max-w-xl mt-1">
                                            When you hit <span className="font-semibold text-[#0C5FD1]">Save & continue</span>, we take you to
                                            channel setup: SMS sender ID, email domains, IVR numbers, wallets and payment rails.
                                        </p>
                                    </div>
                                    <button className="inline-flex items-center gap-1 rounded-full bg-[var(--primary-color)] text-white text-xs font-semibold px-4 py-2 shadow hover:brightness-110 transition">
                                        Save & continue
                                        <span>→</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}

// Small components

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <div className="mt-2 mb-1">
            <p className="text-[11px] font-semibold tracking-wide uppercase text-[#0C5FD1]">{title}</p>
            <p className="text-[10px] text-slate-500">{subtitle}</p>
        </div>
    );
}

function BlockToggle({
    category,
    option,
    selected,
    onToggle,
}: {
    category: keyof typeof blockOptions;
    option: { id: string; name: string; description: string };
    selected: boolean;
    onToggle: (category: keyof typeof blockOptions, id: string) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => onToggle(category, option.id)}
            className={`w-full text-left rounded-xl border px-3 py-2 transition text-[11px] flex flex-col gap-1 ${selected
                    ? "border-[var(--primary-color)]/80 bg-[var(--primary-color)]/10 text-[#062150] shadow-[0_0_0_1px_rgba(15,23,42,0.1)]"
                    : "border-slate-200 bg-white/80 hover:border-[var(--primary-color)]/60 hover:bg-[#F4F8FF]"
                }`}
        >
            <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-[#083570]">{option.name}</p>
                <span
                    className={`h-4 w-4 inline-flex items-center justify-center rounded-md border text-[9px] ${selected
                            ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white"
                            : "border-slate-300 bg-[#F4F8FF] text-slate-400"
                        }`}
                >
                    {selected ? "✓" : "+"}
                </span>
            </div>
            <p className="text-[10px] text-slate-500">{option.description}</p>
        </button>
    );
}

// PREVIEW BUILDING BLOCKS

function HeroA() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-[#F4F8FF] to-[#E4F0FF] p-5 flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1">
                <p className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#E4F0FF]/90 border border-slate-200 text-[10px] text-[#0C5FD1] mb-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Live · Powered by Chamcore microservices
                </p>
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#062150] mb-2">
                    Launch a consult-ready website in minutes.
                </h1>
                <p className="text-sm text-[#0C5FD1] mb-4 max-w-xl">
                    Pick your blocks, connect your phone numbers, video rooms and payment rails. Chamcore takes care of routing,
                    billing and reporting.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 text-xs">
                    <button className="px-4 py-2 rounded-full bg-[var(--primary-color)] text-white font-semibold hover:brightness-110 transition">
                        Book a demo
                    </button>
                    <button className="px-4 py-2 rounded-full border border-slate-300 text-[#0A4A99] hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                        Try a sandbox site
                    </button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-[11px] text-[#0C5FD1]">
                    <div>
                        <p className="font-semibold text-[#083570]">Microcomponents</p>
                        <p className="text-slate-500">Hero, agents, blog, video, CTA, checkout and more.</p>
                    </div>
                    <div>
                        <p className="font-semibold text-[#083570]">Connected stack</p>
                        <p className="text-slate-500">SMS, IVR, WebRTC and payments pre-wired.</p>
                    </div>
                </div>
            </div>
            <div className="w-full md:w-72 flex justify-center">
                <div className="w-full rounded-2xl border border-[var(--primary-color)]/40 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 p-3 shadow-[0_0_40px_rgba(15,23,42,0.16)]">
                    <p className="text-[11px] text-[#0C5FD1] mb-2">Lead capture</p>
                    <div className="space-y-2 text-[11px]">
                        <input
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 outline-none focus:border-[var(--primary-color)] text-[#083570] placeholder:text-slate-400"
                            placeholder="Name"
                        />
                        <input
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 outline-none focus:border-[var(--primary-color)] text-[#083570] placeholder:text-slate-400"
                            placeholder="Email"
                        />
                        <select className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 outline-none focus:border-[var(--primary-color)] text-[#083570] text-[11px]">
                            <option>Channel focus</option>
                            <option>Advisory & coaches</option>
                            <option>Healthcare & telemedicine</option>
                            <option>Legal & compliance</option>
                            <option>Custom</option>
                        </select>
                        <button className="w-full mt-1 rounded-lg bg-[var(--primary-color)] text-white font-semibold py-1.5 hover:brightness-110 transition">
                            Generate my site
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroB() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-5 flex flex-col items-center text-center gap-4">
            <p className="text-[11px] text-[#0C5FD1] uppercase tracking-[0.14em]">Chamcore blueprint · Hero B</p>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#062150] max-w-2xl">
                Drag microcomponents. Ship a fully wired, multi-channel website.
            </h1>
            <p className="text-sm text-[#0C5FD1] max-w-xl">
                Swap hero layouts, agent cards, blogs and streaming blocks without touching code. Your dev team only connects
                the APIs once.
            </p>
            <div className="aspect-video w-full max-w-xl rounded-2xl border border-slate-200 bg-gradient-to-tr from-slate-900 via-slate-800 to-cyan-900/40 flex items-center justify-center text-[11px] text-[#E7F4FF]">
                <div className="px-4 py-2 rounded-full bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 border border-slate-300 flex items-center gap-2 text-[#062150]">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    Demo video – Building a site in 90 seconds
                </div>
            </div>
        </section>
    );
}

function HeroC() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-r from-[var(--primary-color)]/20 via-[#F4F8FF] to-[#E4F0FF] p-5 flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1 space-y-2">
                <p className="text-[11px] text-[#0C5FD1] uppercase tracking-[0.16em]">Hero C · Split layout</p>
                <h1 className="text-2xl md:text-3xl font-semibold text-[#062150]">
                    Templates for advisors, clinics and marketplaces.
                </h1>
                <p className="text-sm text-[#0A4A99] max-w-xl">
                    Pick a pre-built industry template or start from a blank Chamcore blueprint. All include routing, booking, and
                    payment flows.
                </p>
                <ul className="text-[11px] text-[#083570] grid grid-cols-2 gap-2 mt-3">
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Advisors & coaches
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Legal & finance
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Healthcare & telemed
                    </li>
                    <li className="flex items-center gap-1.5">
                        <span className="h-3 w-3 rounded-full bg-[var(--primary-color)]/80" />
                        Custom service hubs
                    </li>
                </ul>
            </div>
            <div className="w-full md:w-64">
                <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF]/80 p-3 space-y-2 text-[11px] text-[#0A4A99]">
                    <p className="font-semibold mb-1">Choose template</p>
                    <div className="space-y-1">
                        <TemplateBadge label="Advisor hub" />
                        <TemplateBadge label="Telehealth pod" />
                        <TemplateBadge label="Legal boutique" />
                        <TemplateBadge label="Custom" />
                    </div>
                </div>
            </div>
        </section>
    );
}

function TemplateBadge({ label }: { label: string }) {
    return (
        <div className="flex items-center justify-between px-2 py-1 rounded-lg border border-slate-200 bg-white">
            <span className="text-[#083570]">{label}</span>
            <span className="text-[10px] text-[#0C5FD1]">Preview</span>
        </div>
    );
}

function AgentsGrid() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Featured advisors</p>
                    <p className="text-[11px] text-slate-500">Auto-syncs from Chamcore agent directory.</p>
                </div>
                <button className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-[#0C5FD1] hover:border-[var(--primary-color)] hover:text-[#0C5FD1] transition bg-white/80">
                    View all
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-[11px]">
                {["Emma", "Jonas", "Lena"].map((name, idx) => (
                    <div key={name} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-full bg-[#9BC3FF]" />
                            <div>
                                <p className="font-semibold text-[#083570]">
                                    {name} {idx === 0 ? "Berg" : idx === 1 ? "Nielsen" : "Holt"}
                                </p>
                                <p className="text-[10px] text-slate-500">Senior advisor · Remote</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            <Tag label="Private clients" />
                            <Tag label="Business" />
                            {idx === 0 && <Tag label="Vipps" />}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                            <button className="flex-1 px-2 py-1 rounded-full bg-[var(--primary-color)] text-white font-semibold">
                                Book
                            </button>
                            <button className="px-2 py-1 rounded-full border border-slate-300 text-[#0A4A99] bg-white/80">
                                Profile
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function AgentsList() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <p className="text-xs font-semibold text-[#083570] mb-2">Our advisors</p>
            <div className="space-y-2 text-[11px]">
                {["Line", "Marius", "Sofie"].map((name, idx) => (
                    <div
                        key={name}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-[#9BC3FF]" />
                            <div>
                                <p className="font-semibold text-[#083570]">
                                    {name} {idx === 0 ? "Hansen" : idx === 1 ? "Lunde" : "Rye"}
                                </p>
                                <p className="text-[10px] text-slate-500">Specialist · Nordic clients</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border border-emerald-500/60 bg-emerald-50 text-emerald-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                Available today
                            </span>
                            <button className="px-2 py-1 rounded-full border border-slate-300 text-[#0A4A99] bg-white/80">
                                Book
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function Tag({ label }: { label: string }) {
    return (
        <span className="px-2 py-0.5 rounded-full border border-slate-300 text-[10px] text-[#0C5FD1] bg-[#E4F0FF]">
            {label}
        </span>
    );
}

function BlogGrid() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Latest insights</p>
                    <p className="text-[11px] text-slate-500">Pulls from /blog API endpoint.</p>
                </div>
                <button className="text-[11px] text-[#0C5FD1]">Manage posts →</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                {[1, 2, 3].map((i) => (
                    <article key={i} className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Advisory</p>
                        <h3 className="font-semibold text-[#083570]">
                            How to move from phone-only to full digital delivery
                        </h3>
                        <p className="text-[10px] text-slate-500">
                            Use Chamcore to add video, chat and payments in one go.
                        </p>
                        <p className="text-[10px] text-slate-400">5 min read · Updated weekly</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

function BlogList() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <p className="text-xs font-semibold text-[#083570] mb-2">From the blog</p>
            <div className="space-y-2 text-[11px]">
                {[1, 2, 3].map((i) => (
                    <article
                        key={i}
                        className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                        <div>
                            <p className="text-[10px] text-slate-500 mb-0.5">CHANNEL DESIGN</p>
                            <h3 className="font-semibold text-[#083570]">Booking, IVR and payments in one UX</h3>
                            <p className="text-[10px] text-slate-500">
                                Why single-channel tools create friction for clients.
                            </p>
                        </div>
                        <p className="text-[10px] text-slate-400 whitespace-nowrap">7 min read</p>
                    </article>
                ))}
            </div>
        </section>
    );
}

function VideoLivestream() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Live sessions</p>
                    <p className="text-[11px] text-slate-500">Chamcore WebRTC + IVR fallback.</p>
                </div>
                <button className="text-[11px] text-[#0C5FD1]">Manage events →</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
                {["Today", "Tomorrow"].map((label, idx) => (
                    <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
                        <p className="text-[10px] text-slate-500 mb-1">{label} · 19:00 CET</p>
                        <h3 className="font-semibold text-[#083570]">Open Q&A with our advisory team</h3>
                        <p className="text-[10px] text-slate-500 mb-2">
                            Join from browser or dial-in via local phone number.
                        </p>
                        <div className="flex items-center justify-between">
                            <button className="px-3 py-1 rounded-full bg-[var(--primary-color)] text-white font-semibold">
                                {idx === 0 ? "Join waiting room" : "Reserve spot"}
                            </button>
                            <p className="text-[10px] text-slate-400">100 seats · 34 booked</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function VideoLibrary() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-semibold text-[#083570]">Video library</p>
                    <p className="text-[11px] text-slate-500">On-demand content powered by Chamcore streaming.</p>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                    <select className="rounded-full border border-slate-300 bg-white px-2 py-1 text-[#0A4A99]">
                        <option>All categories</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <div className="aspect-video bg-[#9BC3FF] flex items-center justify-center text-[10px] text-slate-600">
                            Video thumbnail
                        </div>
                        <div className="p-3 space-y-1">
                            <p className="font-semibold text-[#083570]">How Chamcore routing works</p>
                            <p className="text-[10px] text-slate-500">Short walkthrough of our comms engine.</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function CtaSimple() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-r from-[var(--primary-color)]/30 to-[#E4F0FF] p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
                <p className="text-xs font-semibold text-[#062150]">Ready to switch on digital delivery?</p>
                <p className="text-[11px] text-[#083570] max-w-md">
                    One Chamcore login gives you website, booking, IVR, video and payments in a single stack.
                </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px]">
                <button className="px-4 py-1.5 rounded-full bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] text-[#062150] font-semibold">
                    Talk to sales
                </button>
                <button className="px-4 py-1.5 rounded-full border border-slate-200 text-[#062150] bg-white/80">
                    Explore pricing
                </button>
            </div>
        </section>
    );
}

function CtaMulti() {
    return (
        <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#E7F4FF] to-[#F8FBFF] p-4">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-[#083570]">Pick how you want to start</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                <CtaCard title="Advisors" body="Solo or small teams that want a ready-made advisory funnel." />
                <CtaCard title="Clinics" body="Healthcare teams connecting pods, rooms and remote staff." />
                <CtaCard title="Custom" body="Marketplaces, platforms and bespoke architectures." />
            </div>
        </section>
    );
}

function CtaCard({ title, body }: { title: string; body: string }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col gap-2">
            <p className="font-semibold text-[#083570]">{title}</p>
            <p className="text-[10px] text-slate-500">{body}</p>
            <button className="mt-auto self-start px-3 py-1 rounded-full bg-[var(--primary-color)] text-white font-semibold text-[11px]">
                Choose
            </button>
        </div>
    );
}
